import { useMemo } from 'react';
import styled from 'styled-components';
import { rgba } from 'polished';

import { c00EE91, cFF005C } from '#/colors';
import { Loadable } from '#/components/shared/Loadable';
import { useOpenPositionsView } from '#/features/perpetuals/open-positions-context';

import { TableSpec, createTradeOverviewTable } from './table';
import { Empty, Error, Mono, SideBase, ValueChange, ValuePair } from './atoms';
import ClosePosition from './ClosePosition';

interface Props {
  tableSpec: TableSpec<string>;
}

export default function OpenPositions(props: Props) {
  const { tableSpec } = props;
  const [Table, Row] = useMemo(
    () => createTradeOverviewTable(tableSpec),
    [tableSpec],
  );
  const { status, error, openPositions } = useOpenPositionsView();

  if (status === 'error') {
    return <Error>{error}</Error>;
  }

  const isEmpty = openPositions.length === 0;
  const isLoading = status === 'loading' || status === 'disconnected';

  if (isEmpty) {
    return (
      <Loadable isLoading={isLoading}>
        <Empty>You have no open positions</Empty>
      </Loadable>
    );
  }

  return (
    <Loadable isLoading={isLoading}>
      <Table>
        {openPositions.map((position) => (
          <Row
            key={position.market}
            market={position.market}
            side={sideComponent[position.side]}
            size={<Mono>{position.size}</Mono>}
            notional={<Mono>{position.notional}</Mono>}
            entryPrice={
              <ValuePair
                primary={position.averageEntryPriceUsd}
                secondary={position.averageEntryPrice}
              />
            }
            markPrice={position.markPrice}
            unrealizedPnl={
              <ValueChange direction={position.unrealizedPnlChange}>
                <Mono>{position.unrealizedPnl}</Mono>
              </ValueChange>
            }
            fundingPnl={
              <ValueChange direction={position.fundingPnlChange}>
                <Mono>{position.fundingPnl}</Mono>
              </ValueChange>
            }
            closePosition={<ClosePosition id={position.id} />}
          />
        ))}
      </Table>
    </Loadable>
  );
}

const Side = styled(SideBase)`
  width: 46px;
`;

const SideLong = styled(Side).attrs({ children: 'LONG' })`
  color: ${c00EE91};
  background: ${rgba(c00EE91, 0.2)};
`;

const SideShort = styled(Side).attrs({ children: 'SHORT' })`
  color: ${cFF005C};
  background: ${rgba(cFF005C, 0.2)};
`;

const sideComponent = {
  LONG: <SideLong />,
  SHORT: <SideShort />,
};
